import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { LogIn, Loader2, UserPlus, Sparkles } from 'lucide-react';

const Login = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const redirectTo = searchParams.get('redirect') || '';
  const action = searchParams.get('action') || '';

  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let userData;
      if (mode === 'register') {
        if (!name.trim()) {
          toast.error('Please enter your name');
          setIsLoading(false);
          return;
        }
        userData = await register(name.trim(), email, password);
        toast.success('Account created!', { description: 'Welcome to Kari Future Now Creations™' });
      } else {
        userData = await login(email, password);
        toast.success('Welcome back!');
      }

      // Smart redirect:
      // 1. If a redirect param was passed, send them there
      // 2. Otherwise, admins → /admin, customers → /
      if (redirectTo) {
        // Append action so destination page can auto-trigger subscribe
        const sep = redirectTo.includes('?') ? '&' : '?';
        navigate(action ? `${redirectTo}${sep}action=${action}` : redirectTo);
      } else if (userData?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || `Failed to ${mode}. Please try again.`;
      toast.error(`${mode === 'register' ? 'Sign up' : 'Login'} failed`, { description: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  const isSubscribeFlow = action === 'subscribe';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#11111a] to-[#0a0a0f] pt-24 pb-20 relative overflow-hidden">
      <div className="pointer-events-none absolute top-20 -left-24 w-96 h-96 rounded-full bg-orange-500/15 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -right-24 w-[28rem] h-[28rem] rounded-full bg-red-600/15 blur-[140px]" />

      <div className="max-w-md mx-auto px-4 relative">
        {isSubscribeFlow && (
          <div className="mb-4 rounded-xl border border-orange-500/40 bg-gradient-to-r from-orange-500/15 to-red-600/15 p-4 text-center backdrop-blur" data-testid="subscribe-prompt">
            <Sparkles className="w-5 h-5 text-orange-300 mx-auto mb-1" />
            <p className="text-orange-200 text-sm font-semibold">
              One quick step before we start your AI Auto-Poster subscription
            </p>
            <p className="text-white/60 text-xs mt-1">
              {mode === 'register' ? 'Create your free account →' : 'Log in to continue →'}
            </p>
          </div>
        )}

        <Card className="border border-white/10 bg-white/5 backdrop-blur-md shadow-xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-orange-500/30">
              {mode === 'register' ? <UserPlus className="w-8 h-8 text-white" /> : <LogIn className="w-8 h-8 text-white" />}
            </div>
            <CardTitle className="text-3xl text-white">
              {mode === 'register' ? 'Create Account' : 'Welcome Back'}
            </CardTitle>
            <CardDescription className="text-base text-white/60">
              {mode === 'register' ? 'Join Kari Future Now Creations™' : 'Sign in to your account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/80">Your Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    required
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    disabled={isLoading}
                    data-testid="register-name"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  disabled={isLoading}
                  data-testid="auth-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'Min 8 characters' : 'Your password'}
                  required
                  minLength={8}
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  disabled={isLoading}
                  data-testid="auth-password"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isLoading}
                data-testid="auth-submit-btn"
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold shadow-lg shadow-orange-500/30"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {mode === 'register' ? 'Creating account...' : 'Signing in...'}
                  </>
                ) : (
                  <>
                    {mode === 'register' ? <UserPlus className="w-5 h-5 mr-2" /> : <LogIn className="w-5 h-5 mr-2" />}
                    {mode === 'register' ? 'Create Account' : 'Sign In'}
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              {mode === 'register' ? (
                <p className="text-sm text-white/60">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-orange-300 hover:text-orange-200 font-semibold underline"
                    data-testid="switch-to-login"
                  >
                    Sign in
                  </button>
                </p>
              ) : (
                <p className="text-sm text-white/60">
                  New here?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('register')}
                    className="text-orange-300 hover:text-orange-200 font-semibold underline"
                    data-testid="switch-to-register"
                  >
                    Create an account
                  </button>
                </p>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-white/10 text-center">
              <Link to="/" className="text-xs text-white/40 hover:text-white/70">
                ← Back to home
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-4 text-[11px] text-orange-300/70 flex items-center justify-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Made with Kari Future Now Creations™
        </div>
      </div>
    </div>
  );
};

export default Login;
